import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { navigationByRole, type NavItem } from '@/config/navigation';
import { backdropFade, scalePop, spring } from '@/lib/motion';
import type { StaffRole } from '@/types';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CommandGroup {
  title: string;
  items: NavItem[];
}

const isMac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function buildGroups(nav: { global: NavItem[]; country: NavItem[] }): CommandGroup[] {
  const groups: CommandGroup[] = [];

  const splitSections = (items: NavItem[], fallbackTitle: string) => {
    let currentTitle = fallbackTitle;
    let currentItems: NavItem[] = [];
    items.forEach((item) => {
      if (item.section) {
        if (currentItems.length > 0) {
          groups.push({ title: currentTitle, items: currentItems });
        }
        currentTitle = item.label;
        currentItems = [];
      } else {
        currentItems.push(item);
      }
    });
    if (currentItems.length > 0) {
      groups.push({ title: currentTitle, items: currentItems });
    }
  };

  if (nav.global.length > 0) splitSections(nav.global, 'Plataforma');
  if (nav.country.length > 0) splitSections(nav.country, 'País');

  return groups;
}

function normalize(s: string) {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const role = user?.profiles?.staff?.role as StaffRole | undefined;
  const nav = useMemo(
    () => (role ? navigationByRole[role] : { global: [], country: [] }),
    [role],
  );
  const groups = useMemo(() => buildGroups(nav), [nav]);

  const flatItems = useMemo(() => {
    const q = normalize(query.trim());
    const flat: { groupTitle: string; item: NavItem }[] = [];
    groups.forEach((g) => {
      g.items.forEach((item) => {
        if (!item.href) return;
        if (!q || normalize(item.label).includes(q) || normalize(g.title).includes(q)) {
          flat.push({ groupTitle: g.title, item });
        }
      });
    });
    return flat;
  }, [groups, query]);

  const groupedFiltered = useMemo(() => {
    const byGroup = new Map<string, NavItem[]>();
    flatItems.forEach(({ groupTitle, item }) => {
      if (!byGroup.has(groupTitle)) byGroup.set(groupTitle, []);
      byGroup.get(groupTitle)!.push(item);
    });
    return Array.from(byGroup.entries()).map(([title, items]) => ({ title, items }));
  }, [flatItems]);

  useEffect(() => {
    if (isOpen) {
      const reset = setTimeout(() => {
        setQuery('');
        setActiveIndex(0);
      }, 0);
      document.body.style.overflow = 'hidden';
      const focusT = setTimeout(() => inputRef.current?.focus(), 30);
      return () => {
        clearTimeout(reset);
        clearTimeout(focusT);
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-cmd-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const runCommand = (item: NavItem) => {
    if (!item.href) return;
    onClose();
    navigate(item.href);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = flatItems[activeIndex];
      if (pick) runCommand(pick.item);
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[120] flex items-start justify-center pt-[12vh] px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Paleta de comandos"
        >
          <motion.div
            key="cmd-backdrop"
            variants={backdropFade}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-md"
          />

          <motion.div
            key="cmd-card"
            variants={scalePop}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="relative z-10 flex w-full max-w-xl flex-col overflow-hidden rounded-xl bg-background ring-1 ring-border/60"
            style={{ boxShadow: 'var(--shadow-dialog)' }}
            onKeyDown={handleKeyDown}
          >
            <div className="flex items-center gap-3 border-b border-border px-4 py-3">
              <svg
                className="h-4 w-4 shrink-0 text-muted-foreground"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                placeholder="Buscar páginas, módulos..."
                className="flex-1 bg-transparent text-[15px] text-foreground placeholder:text-muted-foreground outline-none"
                autoComplete="off"
                spellCheck={false}
                aria-label="Buscar comandos"
              />
              <kbd className="hidden items-center gap-1 rounded-md border border-border bg-background-secondary px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline-flex">
                ESC
              </kbd>
            </div>

            <div
              ref={listRef}
              className="max-h-[60vh] overflow-y-auto py-2"
              role="listbox"
            >
              {flatItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-1 py-10 text-center">
                  <p className="text-sm font-medium text-foreground">Sin resultados</p>
                  <p className="text-xs text-muted-foreground">
                    Intenta otro término de búsqueda
                  </p>
                </div>
              ) : (
                groupedFiltered.map((group) => {
                  return (
                    <div key={group.title} className="px-2">
                      <div className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        {group.title}
                      </div>
                      <ul className="space-y-0.5">
                        {group.items.map((item) => {
                          const globalIndex = flatItems.findIndex(
                            (f) => f.item.href === item.href,
                          );
                          const isActive = globalIndex === activeIndex;
                          return (
                            <li key={item.href}>
                              <button
                                type="button"
                                data-cmd-index={globalIndex}
                                onClick={() => runCommand(item)}
                                onMouseEnter={() => setActiveIndex(globalIndex)}
                                className={`group relative flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm cursor-pointer ${
                                  isActive
                                    ? 'bg-muted text-foreground'
                                    : 'text-foreground-secondary'
                                }`}
                                role="option"
                                aria-selected={isActive}
                              >
                                {isActive && (
                                  <motion.span
                                    layoutId="cmd-active"
                                    className="absolute inset-0 rounded-md bg-muted"
                                    transition={spring.responsive}
                                    style={{ zIndex: 0 }}
                                  />
                                )}
                                <span className="relative z-[1] flex h-5 w-5 items-center justify-center text-muted-foreground">
                                  {item.icon}
                                </span>
                                <span className="relative z-[1] flex-1 truncate">
                                  {item.label}
                                </span>
                                {isActive && (
                                  <kbd className="relative z-[1] inline-flex items-center gap-0.5 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                                    <svg
                                      className="h-2.5 w-2.5"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <polyline points="9 10 4 15 9 20" />
                                      <path d="M20 4v7a4 4 0 0 1-4 4H4" />
                                    </svg>
                                  </kbd>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })
              )}
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-border bg-background-secondary/50 px-4 py-2.5 text-[11px] text-muted-foreground">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-1">
                  <kbd className="rounded border border-border bg-background px-1 py-0.5 font-mono">
                    ↑
                  </kbd>
                  <kbd className="rounded border border-border bg-background px-1 py-0.5 font-mono">
                    ↓
                  </kbd>
                  navegar
                </span>
                <span className="inline-flex items-center gap-1">
                  <kbd className="rounded border border-border bg-background px-1 py-0.5 font-mono">
                    ↵
                  </kbd>
                  abrir
                </span>
              </div>
              <span className="inline-flex items-center gap-1">
                <kbd className="rounded border border-border bg-background px-1 py-0.5 font-mono">
                  {isMac ? '⌘' : 'Ctrl'}
                </kbd>
                <kbd className="rounded border border-border bg-background px-1 py-0.5 font-mono">
                  K
                </kbd>
              </span>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
